/**
 * Custom fetch mutator used by the Orval-generated client.
 * Same-origin requests: the dev preview and production server both serve
 * the API under /api, so no base URL is required.
 *
 * When authentication is enabled the frontend registers a token provider
 * (see setAuthTokenProvider); the mutator then attaches a bearer token to
 * every request. Tokens stay in memory — never persisted here.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Full parsed JSON error body (may contain extra fields beyond error/code). */
  readonly body: Record<string, unknown> | undefined;

  constructor(message: string, status: number, code?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type AuthTokenProvider = () => Promise<string | null>;

let tokenProvider: AuthTokenProvider | null = null;

/** Register (or clear) the function used to obtain bearer tokens. */
export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  tokenProvider = provider;
}

/** Resolve the current bearer token, or null when auth is disabled. */
export async function getAuthToken(): Promise<string | null> {
  if (!tokenProvider) return null;
  try {
    return await tokenProvider();
  } catch {
    return null;
  }
}

/**
 * Acting-tenant provider — used by the super_admin council switcher.
 * When set, every workspace API request includes the X-Acting-Tenant-Id header
 * so the backend resolves queries to the selected council.
 */
export type ActingTenantProvider = () => string | null;

let actingTenantProvider: ActingTenantProvider | null = null;

/** Register (or clear) the function that returns the acting tenant ID. */
export function setActingTenantProvider(provider: ActingTenantProvider | null): void {
  actingTenantProvider = provider;
}

/**
 * Acting-role provider — used by the staff role switcher.
 * When set, every API request includes the X-Acting-Role header so the
 * backend clamps permissions to the selected (lower) role.
 */
export type ActingRoleProvider = () => string | null;

let actingRoleProvider: ActingRoleProvider | null = null;

/** Register (or clear) the function that returns the acting role. */
export function setActingRoleProvider(provider: ActingRoleProvider | null): void {
  actingRoleProvider = provider;
}

async function buildHeaders(options: RequestInit): Promise<HeadersInit> {
  const isJsonBody = typeof options.body === 'string';
  const token = await getAuthToken();
  const actingTenantId = actingTenantProvider?.() ?? null;
  const actingRole = actingRoleProvider?.() ?? null;
  return {
    Accept: 'application/json',
    ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(actingTenantId ? { 'X-Acting-Tenant-Id': actingTenantId } : {}),
    ...(actingRole ? { 'X-Acting-Role': actingRole } : {}),
    ...options.headers
  };
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error ? String((error as { name?: unknown }).name) : '';
  return name === 'AbortError';
}

export const customFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  // Drop the React Query abort signal for idempotent GETs. StrictMode remounts
  // cancel in-flight observers and the preview network collector surfaces those
  // as hard AbortError failures even though the app recovers on the next mount.
  const method = (options.method ?? 'GET').toUpperCase();
  const { signal, ...rest } = options;
  const requestInit: RequestInit = {
    ...rest,
    headers: await buildHeaders(rest),
    ...(method === 'GET' || method === 'HEAD' ? {} : signal ? { signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(url, requestInit);
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw a quiet abort so callers can ignore without unhandledrejection noise.
      const quiet = new DOMException('Request aborted', 'AbortError');
      throw quiet;
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const body = isJson
      ? ((await response.json().catch(() => undefined)) as
          | { error?: string; code?: string }
          | undefined)
      : undefined;
    throw new ApiError(
      body?.error ?? `Request failed with status ${response.status}`,
      response.status,
      body?.code,
      body
    );
  }

  if (!isJson) {
    // Binary endpoints (file download) resolve to a Blob.
    return (await response.blob()) as T;
  }

  return (await response.json()) as T;
};
